import React, { useCallback, useMemo, useState } from "react";
import { Modal } from "react-bootstrap";
import SessionManager from "../Utils/SessionManager";
import COLORS from "../resources/colors/COLORS";

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

function normalizeMonthCode(raw) {
  if (!raw) return "";
  const m = String(raw).trim().match(/^(\d{4})-(\d{1,2})/);
  if (!m) return String(raw).trim();
  return `${m[1]}-${String(parseInt(m[2], 10)).padStart(2, "0")}`;
}

function parseSessionStartDate(raw) {
  if (!raw) return null;
  const value = String(raw).trim();
  let m = value.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (m) return new Date(+m[1], +m[2] - 1, +m[3]);
  m = value.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})/);
  if (m) return new Date(+m[3], +m[2] - 1, +m[1]);
  const fallback = new Date(value);
  return Number.isNaN(fallback.getTime()) ? null : fallback;
}

function defaultAcademicStart() {
  const now = new Date();
  const year = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
  return new Date(year, 3, 1);
}

function resolveSessionStart() {
  const session = SessionManager.getCurrentSession()?.[0];
  return (
    parseSessionStartDate(session?.start_date) ||
    parseSessionStartDate(
      typeof localStorage !== "undefined"
        ? localStorage.getItem("START_DATE")
        : null
    ) ||
    defaultAcademicStart()
  );
}

function generateSessionMonths() {
  const start = resolveSessionStart();
  const y0 = start.getFullYear();
  const m0 = start.getMonth();
  const months = [];
  for (let i = 0; i < 12; i++) {
    const d = new Date(y0, m0 + i, 1);
    const month = d.getMonth() + 1;
    const year = d.getFullYear();
    months.push({
      name: MONTH_NAMES[d.getMonth()],
      year: String(year),
      code: `${year}-${month < 10 ? `0${month}` : month}`,
    });
  }
  return months;
}

const MdlAddServiceMonth = ({ existingMonths = [], onSave, actionClose }) => {
  const [show, setShow] = useState(true);
  const [selectedMonths, setSelectedMonths] = useState([]);
  const months = useMemo(() => generateSessionMonths(), []);

  const existingSet = useMemo(() => {
    const set = new Set();
    (existingMonths || []).forEach((code) => {
      const normalized = normalizeMonthCode(code);
      if (normalized) set.add(normalized);
    });
    return set;
  }, [existingMonths]);

  const availableMonths = useMemo(
    () => months.filter((m) => !existingSet.has(m.code)),
    [months, existingSet]
  );

  const handleClose = useCallback(() => {
    setShow(false);
    if (typeof actionClose === "function") actionClose();
  }, [actionClose]);

  const handleDone = useCallback(() => {
    if (!selectedMonths.length) return;
    if (typeof onSave === "function") onSave(selectedMonths);
    handleClose();
  }, [selectedMonths, onSave, handleClose]);

  const toggleMonthSelection = useCallback((monthCode) => {
    setSelectedMonths((prev) =>
      prev.includes(monthCode)
        ? prev.filter((m) => m !== monthCode)
        : [...prev, monthCode]
    );
  }, []);

  const toggleAll = useCallback(() => {
    setSelectedMonths((prev) =>
      prev.length === availableMonths.length
        ? []
        : availableMonths.map((m) => m.code)
    );
  }, [availableMonths]);

  const allSelected =
    availableMonths.length > 0 &&
    selectedMonths.length === availableMonths.length;

  const canAdd = availableMonths.length > 0 && selectedMonths.length > 0;

  return (
    <>
      <style>{`
        .mdl-atsm .modal-content {
          border-radius: 14px !important;
          border: 1.5px solid #81d4fa !important;
          overflow: hidden;
        }
        .mdl-atsm .modal-header .close {
          outline: none;
        }
        .mdl-atsm .mdl-atsm__actions {
          display: flex;
          gap: 8px;
          align-items: center;
        }
        /* Native buttons — avoid global .btn { background-image: none !important } */
        .mdl-atsm .mdl-atsm__cancel,
        .mdl-atsm .mdl-atsm__add {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-height: 36px;
          padding: 6px 16px;
          border-radius: 999px;
          font-size: 13px;
          font-weight: 750;
          cursor: pointer;
          margin: 0;
          -webkit-appearance: none;
          appearance: none;
        }
        .mdl-atsm .mdl-atsm__cancel {
          color: ${COLORS.primary_color_shade9} !important;
          background-color: #ffffff !important;
          background-image: none !important;
          border: 1.5px solid ${COLORS.primary_color_shade2} !important;
        }
        .mdl-atsm .mdl-atsm__cancel:hover {
          background-color: ${COLORS.primary_color_shade0} !important;
          color: ${COLORS.primary_color_shade9} !important;
        }
        .mdl-atsm .mdl-atsm__add {
          color: #ffffff !important;
          background-color: ${COLORS.primary_color_shade5} !important;
          background-image: linear-gradient(135deg, ${COLORS.primary_color_shade5} 0%, ${COLORS.primary_color_shade7} 100%) !important;
          border: 1.5px solid ${COLORS.primary_color_shade7} !important;
          box-shadow: 0 4px 12px rgba(3, 169, 244, 0.28) !important;
        }
        .mdl-atsm .mdl-atsm__add:hover:not(:disabled) {
          color: #ffffff !important;
          background-color: ${COLORS.primary_color_shade6} !important;
          background-image: linear-gradient(135deg, ${COLORS.primary_color_shade4} 0%, ${COLORS.primary_color_shade7} 100%) !important;
        }
        .mdl-atsm .mdl-atsm__add:disabled {
          color: #ffffff !important;
          background-color: ${COLORS.primary_color_shade3} !important;
          background-image: none !important;
          border-color: ${COLORS.primary_color_shade3} !important;
          opacity: 0.9;
          cursor: not-allowed;
          box-shadow: none !important;
        }
      `}</style>
      <Modal
        show={show}
        onHide={handleClose}
        animation={false}
        centered
        dialogClassName="mdl-atsm"
      >
        <Modal.Header
          closeButton
          style={{
            borderBottom: `1.5px solid ${COLORS.primary_color_shade1}`,
            padding: "12px 16px",
            background: `linear-gradient(180deg, ${COLORS.primary_color_shade0} 0%, #fff 100%)`,
          }}
        >
          <div style={{ width: "100%", paddingRight: 12 }}>
            <h5
              style={{
                margin: 0,
                fontWeight: 800,
                fontSize: "15px",
                color: COLORS.primary_color_shade9,
              }}
            >
              Add Transport Service Months
            </h5>
            <p
              style={{
                margin: "3px 0 0 0",
                fontSize: "12px",
                color: "#334155",
                fontWeight: 600,
              }}
            >
              Select months to enable transport fee for this student.
            </p>
          </div>
        </Modal.Header>

        <Modal.Body style={{ padding: "12px 16px 8px" }}>
          {availableMonths.length === 0 ? (
            <div
              style={{
                padding: "14px",
                borderRadius: "10px",
                border: `1.5px dashed ${COLORS.primary_color_shade2}`,
                backgroundColor: COLORS.primary_color_shade0,
                fontSize: "13px",
                color: "#334155",
                fontWeight: 650,
                textAlign: "center",
              }}
            >
              All months for the current session are already added for this
              student.
            </div>
          ) : (
            <>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: 8,
                }}
              >
                <span
                  style={{
                    fontSize: "12px",
                    fontWeight: 700,
                    color: COLORS.primary_color_shade8,
                  }}
                >
                  Session months
                </span>
                <button
                  type="button"
                  onClick={toggleAll}
                  style={{
                    border: `1.5px solid ${COLORS.primary_color_shade2}`,
                    background: COLORS.primary_color_shade0,
                    color: COLORS.primary_color_shade8,
                    fontSize: "11px",
                    fontWeight: 800,
                    padding: "3px 10px",
                    borderRadius: 999,
                    cursor: "pointer",
                  }}
                >
                  {allSelected ? "Clear all" : "Select all"}
                </button>
              </div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
                  gap: "8px",
                  padding: "2px 0 8px",
                }}
              >
                {availableMonths.map((item) => {
                  const isSelected = selectedMonths.includes(item.code);
                  return (
                    <button
                      key={item.code}
                      type="button"
                      onClick={() => toggleMonthSelection(item.code)}
                      aria-pressed={isSelected}
                      style={{
                        padding: "9px 6px",
                        borderRadius: 999,
                        cursor: "pointer",
                        border: isSelected
                          ? `2px solid ${COLORS.primary_color_shade5}`
                          : `1.5px solid ${COLORS.primary_color_shade2}`,
                        background: isSelected
                          ? COLORS.primary_color_shade0
                          : "#ffffff",
                        boxShadow: isSelected
                          ? "0 3px 10px rgba(3, 169, 244, 0.25)"
                          : "none",
                        transition:
                          "border-color 0.15s ease, background 0.15s ease",
                        textAlign: "center",
                      }}
                    >
                      <div
                        style={{
                          fontSize: "13px",
                          fontWeight: 800,
                          color: isSelected
                            ? COLORS.primary_color_shade9
                            : "#0f172a",
                          lineHeight: 1.2,
                        }}
                      >
                        {item.name.substring(0, 3)}
                      </div>
                      <div
                        style={{
                          fontSize: "11px",
                          fontWeight: 650,
                          color: "#334155",
                          marginTop: 2,
                        }}
                      >
                        {item.year}
                      </div>
                    </button>
                  );
                })}
              </div>
            </>
          )}
        </Modal.Body>

        <Modal.Footer
          style={{
            borderTop: `1.5px solid ${COLORS.primary_color_shade1}`,
            padding: "10px 16px 12px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            background: COLORS.primary_color_shade0,
          }}
        >
          <div
            style={{
              fontSize: "12px",
              fontWeight: 700,
              color: selectedMonths.length
                ? COLORS.primary_color_shade8
                : "#64748b",
            }}
          >
            {selectedMonths.length > 0
              ? `${selectedMonths.length} month${
                  selectedMonths.length > 1 ? "s" : ""
                } selected`
              : "No months selected"}
          </div>
          <div className="mdl-atsm__actions">
            <button
              type="button"
              className="mdl-atsm__cancel"
              onClick={handleClose}
            >
              Cancel
            </button>
            <button
              type="button"
              className="mdl-atsm__add"
              onClick={handleDone}
              disabled={!canAdd}
            >
              Add Months
            </button>
          </div>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default MdlAddServiceMonth;
